import Promise from 'bluebird';
import _ from 'underscore';
import { Subject as Observable } from '@bingads-webui-universal/observer-pattern';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { JsDataQueryConverter } from './utils/js-data-query-generator';

export class QueryBuilder extends Observable {
  /**
   * Creates an instance of QueryBuilder.
   * @param {Object} Options - the options to create the data query builder
   * @memberof QueryBuilder
   */
  constructor({
    decorators,
    initialQuery = {},
    builderConfig = {
      generateJsDataQuery: false,
      featureName: '',
    },
    logActivity = {
      error: _.noop,
    },
  }) {
    super();

    this.decorators = decorators;
    this.initialQuery = initialQuery;
    this.featureName = builderConfig.featureName;
    this.generateJsDataQuery = builderConfig.generateJsDataQuery;
    this.lastQuery = null;
    this.logActivity = logActivity;
  }

  /**
   * Called every time new query is generated.
   * Triggers event 'queryChanged' if query has changed.
   * @private
   * @param {Object} newQuery - The query
   * @returns {Object} - The query
   */
  handleNewQuery(newQuery) {
    let query = newQuery;
    delete query.bodyParameters;

    if (this.generateJsDataQuery) {
      query = JsDataQueryConverter.convert(query);
    }

    if (!_.isEqual(this.lastQuery, newQuery)) {
      this.notify('queryChanged', newQuery);
    }
    this.lastQuery = query;

    return query;
  }

  /**
   * Pipes the query through the decorators
   * @returns {Object} - the transformed query
   */
  getQuery() {
    const initialQuery = cloneDeep(this.initialQuery);

    return Promise.reduce(
      this.decorators,
      (dataQuery, decorator) => decorator.run(dataQuery),
      initialQuery
    )
      .then(this.handleNewQuery.bind(this))
      .catch((e) => {
        this.logActivity.error(`Error while building query for ${this.featureName}: ${e.stack ? e.stack : e}`);
        return Promise.reject(e);
      });
  }

  getDecoratorByName(name) {
    return _.findWhere(this.decorators, { name });
  }
}
